<template>
  <div>
    <ul class="drawer__menu">
      <li
        v-for="(item, index) in items"
        :key="`item-${index + 1}`"
        v-show="item.visible"
      >
        <!-- without subitems -->
        <router-link v-if="!item.subitems" :to="{ name: item.route.name }">
          <v-tooltip :disabled="!miniDrawer" right>
            <template v-slot:activator="{ on }">
              <span v-on="on" class="drawer__menu__item__icon" :class="`drawer__menu__item__icon--${item.icon}`" />
              <span v-on="on" class="drawer__menu__item__text">{{ item.title | myLocale('Components.MainMenu') }}</span>
            </template>
            <span>{{ item.title | myLocale('Components.MainMenu') }}</span>
          </v-tooltip>
        </router-link>

        <!-- with subitems -->
        <div
          v-if="item.subitems"
          class="drawer__submenu__container"
          :class="{ 'drawer__submenu__container--open': item.path === routePath }"
        >
          <a
            href="#"
            :data-id="`item-${index}`"
            @click.prevent="toggleDropdown(`item-${index}`)"
          >
            <v-tooltip :disabled="!miniDrawer" right>
              <template v-slot:activator="{ on }">
                <span v-on="on" class="drawer__menu__item__icon" :class="`drawer__menu__item__icon--${item.icon}`" />
                <span v-on="on">{{ item.title | myLocale('Components.MainMenu') }}</span>
                <v-icon v-on="on" class="drawer__menu__item__arrow" small>keyboard_arrow_right</v-icon>
              </template>
              <span>{{ item.title | myLocale('Components.MainMenu') }}</span>
            </v-tooltip>
          </a>

          <ul v-if="!miniDrawer" class="drawer__submenu">
            <li
              v-for="(subitem, index) in item.subitems"
              :key="`subitem-${(index + 1)}`"
              v-show="subitem.visible"
            >
              <router-link :to="{ name: subitem.route.name }">
                {{ subitem.title | myLocale('Components.MainMenu') }}
              </router-link>
            </li>
          </ul>
        </div>
      </li>
    </ul>

    <div class="drawer__menu__divisor" />

    <ul v-if="!miniDrawer" class="drawer__menu--secondary" style="margin-bottom: 6px">
      <li v-show="tokenRequestPermission">
        <router-link :to="{ name: 'TokenRequest' }">
          <span>{{ 'requestApiToken' | myLocale('Components.MainMenu') }}</span>
        </router-link>
      </li>
      <li>
        <a @click.prevent="handleClickFaq">
          <span>{{ 'faq' | myLocale('Components.MainMenu') }}</span>
          <span v-if="!!csMsgCount" class="cs_msg_count">{{csMsgCount}}</span>
        </a>
      </li>
    </ul>

    <div v-show="miniDrawer" class="main-menu__arrow--down">
      <v-icon color="gray-4">keyboard_arrow_down</v-icon>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import commonService from '@/services/common'
import { hasRole } from 'utils/functions'
import axios from 'axios'
import {
  SESSION_HEADERS
} from 'utils/constants'
const msgTimer = null

export default {
  name: 'MenuItems',
  props: {
    miniDrawer: {
      type: Boolean
    }
  },
  data () {
    return {
      csMsgCount: 0
    }
  },
  mounted () {
    this.onTimerMsg()
  },
  computed: {
    ...mapState({
      claims: ({ userClaims }) => userClaims.claims,
      user: state => state.session.user.business
    }),

    routePath () {
      return this.$route.meta.path
    },

    tokenRequestPermission () {
      return hasRole('custom', 'ROLE_POST_REQUEST_API_TOKEN')
    },

    items () {
      return [
        {
          title: 'employees',
          icon: 'employees',
          route: { name: 'Employees' },
          visible: hasRole('menu', 'ROLE_MENU_EMPLOYEES')
        },
        {
          title: 'users',
          icon: 'star',
          route: { name: 'Users' },
          visible: hasRole('menu', 'ROLE_MENU_USERS')
        },
        {
          title: 'config',
          icon: 'config',
          path: 'config',
          visible: hasRole('menu', 'ROLE_MENU_COMPANY') ||
            hasRole('menu', 'ROLE_MENU_COSTCENTER') ||
            hasRole('menu', 'ROLE_MENU_PROJECTS') ||
            hasRole('menu', 'ROLE_MENU_USAGEPOLICIES') ||
            hasRole('employee', 'ROLE_GET_EMPLOYEE_RIDECREDIT'),
          subitems: [
            {
              title: 'company',
              route: { name: 'Company' },
              visible: hasRole('menu', 'ROLE_MENU_COMPANY')
            },
            {
              title: 'costCenter',
              route: { name: 'CostCenters' },
              visible: hasRole('menu', 'ROLE_MENU_COSTCENTER')
            },
            {
              title: 'projects',
              route: { name: 'Projects' },
              visible: hasRole('menu', 'ROLE_MENU_PROJECTS')
            },
            {
              title: 'usagePolicies',
              route: { name: 'UsagePolicies' },
              visible: hasRole('menu', 'ROLE_MENU_USAGEPOLICIES')
            },
            {
              title: 'creditPolicies',
              route: { name: 'CreditPolicies' },
              visible: hasRole('employee', 'ROLE_GET_EMPLOYEE_RIDECREDIT')
            },
            {
              title: 'incentives',
              route: { name: 'Incentives' },
              visible: hasRole('billing', 'ROLE_GET_INCENTIVES') || hasRole('billing', 'ROLE_GET_FINANCIAL_ACCOUNT_INCENTIVES')
            }
          ]
        },
        {
          title: 'reports',
          icon: 'reports',
          route: { name: 'Reports' },
          visible: hasRole('menu', 'ROLE_MENU_REPORTS')
        },
        {
          title: 'financial',
          icon: 'financial',
          path: 'financial',
          visible: hasRole('menu', 'ROLE_MENU_FINANCIAL') ||
            (hasRole('menu', 'ROLE_MENU_FINANCIAL_ADJUSTMENT')) ||
            hasRole('financial', 'ROLE_GET_ORDER_REPROCESS') ||
            hasRole('custom', 'ROLE_POST_PROCESS') ||
            hasRole('financial', 'ROLE_GET_FINANCE_SUMMARY'),
          subitems: [
            {
              title: 'invoiceHistory',
              route: { name: 'Financial' },
              visible: hasRole('menu', 'ROLE_MENU_FINANCIAL')
            },
            {
              title: 'financialAdjustments',
              route: { name: 'FinancialAdjustments' },
              visible: hasRole('menu', 'ROLE_MENU_FINANCIAL_ADJUSTMENT')
            },
            {
              title: 'manualRetrieval',
              route: { name: 'ManualRetrieval' },
              visible: hasRole('financial', 'ROLE_GET_ORDER_REPROCESS')
            },
            {
              title: 'importNfe',
              route: { name: 'ImportNfe' },
              visible: hasRole('custom', 'ROLE_POST_PROCESS')
            },
            {
              title: 'revenues',
              route: { name: 'Revenues' },
              visible: hasRole('financial', 'ROLE_GET_FINANCE_SUMMARY')
            },
            {
              title: 'approvePayment',
              route: { name: 'ApprovePayment' },
              visible: hasRole('billing', 'ROLE_GET_PAGINATED_ORDERS') || hasRole('billing', 'ROLE_GET_MASSIVE_ORDER_PAYMENT_STATUS_WORKER')
            }
          ]
        },
        {
          title: 'searchByPhone',
          icon: 'phone',
          route: { name: 'SearchPhonePage' },
          visible: hasRole('menu', 'ROLE_MENU_CELPHONE_SEARCH')
        }
      ]
    }
  },
  methods: {
    handleMiniDrawer () {
      if (window.innerWidth >= 768 && this.miniDrawer) {
        this.$emit('handleMiniDrawer')
      }
    },

    toggleDropdown (elemId) {
      const submenu = document.querySelector(`[data-id=${elemId}]`).parentNode
      if (!submenu.classList.contains('drawer__submenu__container--open')) {
        const items = document.querySelectorAll('.drawer__submenu__container')
        items.forEach(item => item.classList.remove('drawer__submenu__container--open'))
        submenu.classList.add('drawer__submenu__container--open')
        this.handleMiniDrawer()
        return
      }
      submenu.classList.remove('drawer__submenu__container--open')
    },

    createHiddenElement (url, target = '_blank') {
      let hiddenElement = document.createElement('a')
      hiddenElement.href = url
      hiddenElement.target = target
      hiddenElement.click()
      hiddenElement.remove()
    },

    handleClickFaq () {
      const _userUUid = String(this.user.legacyUserId)
      if (['1464031'].includes(_userUUid)) {
        const sessionStr = localStorage.getItem(SESSION_HEADERS)
        const sessionObj = sessionStr ? JSON.parse(sessionStr) : {}
        window.open(`https://entrega.didiglobal.com?t=${new Date().getTime()}/#/csPage?authorization=${sessionObj.authorization}`)
      } else {
        this.$ga.page('/faq')
        this.createHiddenElement('https://99empresas.zendesk.com')
      }
    },

    onTimerMsg () {
      const _taht = this
      function getMsgCount (uid) {
        if (!uid) return
        /** 这个接口是B2C的客服用的。实际上客服接入到一半不接了，并且V3中B2C即将下线，这边就不改了 */
        axios({
          url: `https://help.didiglobal.com/app/api/getCsMsg?uid=${uid}`,
          methods: 'GET'
        }).then(res => {
          const body = res.data || {}
          const data = body.data || {}
          _taht.csMsgCount = data.csMsgCount || 0
        })
      }
      const commonparams = {
        lang: 'pt-BR',
        locale: 'pt_BR',
        location_country: 'BR'
      }
      commonService.getCxConfig({
        headers: {
          authorization: this.getAuthorization(),
          'didi-header-hint-content': JSON.stringify(commonparams)
        }
      }).then(res => {
        const configBo = (res.data && res.data.data) || {}
        const uid = configBo.uid
        if (uid) {
          if (msgTimer) clearInterval(msgTimer)
          getMsgCount(uid)
          window.setInterval(() => {
            getMsgCount(uid)
          }, 10 * 1000)
        }
      }).catch(() => {})
    },

    getAuthorization () {
      const sessionStr = localStorage.getItem(SESSION_HEADERS)
      const sessionObj = sessionStr ? JSON.parse(sessionStr) : {}
      return sessionObj.authorization
    }
  }
}
</script>

<style lang="scss" scoped>
$icons: ('dashboards', 'employees', 'star', 'config', 'cost-center', 'projects', 'reports', 'financial', 'phone', 'star');
$icon-size: 24px;

@mixin showMenuItemBorderPlaceholder() {
  &:before,
  &:after {
    opacity: 1;
    visibility: visible;
  }
}

@mixin menuItemBorderPlaceholder () {
  &:before {
    background-image: linear-gradient(354deg, $accent, $accent-2);
    bottom: 0;
    right: 0;
    top: 0;
    width: 4px;
    z-index: 1;
  }

  &:after {
    border-bottom: 4px solid transparent;
    border-left: 4px solid #1a1b1a;
    border-right: 4px solid transparent;
    border-top: 4px solid transparent;
    right: -3px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
  }
}

.cs_msg_count {
  display:block;
  background:red;
  width:20px;
  height:20px;
  position:absolute;
  top:3px;
  left:130px;
  border-radius:20px;
  color:#fff;
  font-size:15px;
  font-weight: 500;
  text-align:center;
  line-height:20px
}

.drawer__menu__item__icon {
  background-position: center center;
  background-repeat: no-repeat;
  display: inline-block;
  height: $icon-size;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  min-width: $icon-size;
  position: absolute;
  width: $icon-size;
  z-index: 999;

  &:before,
  &:after {
    background-size: 100%;
    content: '';
    display: block;
    height: 24px;
    position: absolute;
    transition: opacity 0.15s ease-in, visibility 0.15s ease-in;
    width: 24px;
  }

  &:before {
    opacity: 1;
    visibility: visible;
  }

  &:after {
    opacity: 0;
    visibility: hidden;
  }
}

@each $icn in $icons {
  .drawer__menu__item__icon--#{$icn} {

    &:before { background-image: url('~@/assets/icons/icn_#{$icn}-gray.svg'); }
    &:after { background-image: url("~@/assets/icons/icn_#{$icn}-orange.svg"); }
  }
}

ul.drawer__menu {
  list-style-type: none;
  padding: 10px 0 0;

  li {

    a {
      border-radius: 30px 0 0 30px;
      color: $gray-3;
      cursor: pointer;
      display: block;
      font-weight: 400;
      margin-left: 17px;
      padding: 12px 50px 12px 56px;
      position: relative;
      text-decoration: none;
      transition: background .15s ease-in, color .15s ease-in;
      white-space: nowrap;

      &:before,
      &:after {
        content: '';
        opacity: 0;
        position: absolute;
        transition: color 0.15s, opacity 0.15s ease-in, visibility 0.15s ease-in;
        visibility: hidden;
      }

      @include menuItemBorderPlaceholder();

      .drawer__menu__item__text {
        display: inline-block;
        vertical-align: middle;
      }

      &:hover,
      &.router-link-active {
        background-color: rgba(#000, .3);
        color: #fff;
      }

      &.router-link-active {
        cursor: default;
        font-weight: 600;

        @include showMenuItemBorderPlaceholder();

        .drawer__menu__item__icon {

          &:before {
            opacity: 0;
            visibility: hidden;
          }

          &:after {
            opacity: 1;
            visibility: visible;
          }
        }
      }

      .drawer__menu__item__arrow {
        position: absolute;
        right: 24px;
        top: 50%;
        transform: translateY(-50%) rotate(0);
      }
    }
  }
}

ul.drawer__submenu {
  display: none;
  list-style-type: none;
  padding: 0 0 0 32px;

  li {

    a {
      padding: 10px 8px 10px 24px;
    }
  }
}

.drawer__submenu__container {

  &.drawer__submenu__container--open {

    > a {
      /* background-color: rgba(#000, .3); */
      color: #fff;

      /* @include showMenuItemBorderPlaceholder(); */
      /* @include menuItemBorderPlaceholder(); */

      .drawer__menu__item__arrow { transform: translateY(-50%) rotate(90deg); }

      .drawer__menu__item__icon {

        &:before {
          opacity: 0;
          visibility: hidden;
        }

        &:after {
          opacity: 1;
          visibility: visible;
        }
      }
    }

    ul.drawer__submenu { display: block; }
  }
}

.drawer__menu--secondary {
  padding: 0;

  li {

    a {
      border-radius: 30px 0 0 30px;
      color: $gray-3;
      cursor: pointer;
      display: block;
      font-weight: 400;
      margin-left: 17px;
      padding: 12px 8px;
      position: relative;
      text-decoration: none;
      transition: background .15s ease-in, color .15s ease-in;
      white-space: nowrap;

      &:hover {
        background-color: rgba(#000, .3);
        color: #fff;
      }
    }
  }
}

.drawer__menu__divisor {
  border: 1px solid $gray-7;
  margin: 18px 24px;
}

.main-menu__arrow--down {
  cursor: pointer;
  text-align: center;
}
</style>
